import { useEffect, useMemo, useState } from "react";
import { message, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import styles from "../DeclineRequestModal/stylesDeclineRequestModal.module.css";

import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { handleWithdrawalReasonsConfig } from "../../../../utils/UserUtils";

function ApproveRequestModal() {
  const [approvalReason, setApprovalReason] = useState(null);
  const [error, setError] = useState(false);
  const [touched, setTouched] = useState(false);

  const { t } = useTranslation();
  const { appConfig } = useSelector((state) => state.config);
  const withdrawalApprovedReasonsConfig = appConfig?.withdrawal_approved_reasons;

  const defaultApprovalReasons = useMemo(
    () => [
      { value: "Sportsbook", label: t("Sportsbook") },
      { value: "Casino", label: t("Casino") },
      { value: "Both", label: t("Both") },
    ],
    [t],
  );

  const approvalReasons =
    handleWithdrawalReasonsConfig(withdrawalApprovedReasonsConfig) ||
    defaultApprovalReasons;

  const {
    isApproveModalOpen,
    setIsApproveModalOpen,
    handleApproved,
    recordApproved,
    approveTransactionLoading,
  } = useWithdrawalReview();

  const reasonValid = Boolean(approvalReason);

  const handleClose = () => {
    setIsApproveModalOpen(false);
  };

  const handleSave = async () => {
    if (!approvalReason || !recordApproved) {
      setError(true);
      setTouched(true);
      return;
    }

    try {
      message.info(AppStrings.REQUEST_BEING_PROCESSED);
      await handleApproved({
        user_id: recordApproved.user_id,
        transaction_id: recordApproved.transaction_id,
        reason: approvalReason,
      });
      message.success(AppStrings.WITHDRAWAL_APPROVED);
    } catch (err) {
      // notification handled in context
    }
  };

  useEffect(() => {
    setError(!reasonValid);
  }, [reasonValid]);

  useEffect(() => {
    if (isApproveModalOpen) {
      setApprovalReason(null);
      setError(false);
      setTouched(false);
    }
  }, [isApproveModalOpen]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2>{AppStrings.APPROVE_MODAL_TITLE}</h2>
        </div>
        <CloseOutlined onClick={handleClose} className={styles.closeIcon} />
      </div>

      <div className={styles.divider} />

      <div className={styles.content}>
        <div className={styles.reason}>
          <p>{AppStrings.APPROVAL_REASON}</p>
        </div>
        <div className={styles.transactionItem}>
          <Select
            className={styles.selectField}
            value={approvalReason}
            onChange={(value) => setApprovalReason(value)}
            onBlur={() => setTouched(true)}
            options={approvalReasons}
            placeholder={AppStrings.SELECT_A_REASON}
            allowClear
            virtual={false}
          />
        </div>
        {error && touched && (
          <div>
            <span className={styles.errorMessage}>
              {AppStrings.FIELD_IS_REQUIRED?.replace(
                "{title}",
                AppStrings.APPROVAL_REASON,
              )}
            </span>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.buttons}>
          <SaveButton
            cancelButton
            cancelButtonText={AppStrings.CLOSE}
            callbackFunction={handleSave}
            callbackFunctionCancel={handleClose}
            isDisabled={error}
            isApiRunning={approveTransactionLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default ApproveRequestModal;
